import { prepareCloudEventPayload } from '~/utils/prepare-cloud-event-payload.ts';
import logger from '~/services/logger';
import { createChallenge } from './utils.ts';
import { ActionResult } from '~/hooks/actions/enums';
import type { ActionHandler } from '~/hooks/actions/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export enum FlowType {
  MATH_CHALLENGE = 'MATH_CHALLENGE',
  REMOTE_VERIFY = 'REMOTE_VERIFY',
  SET_PIN = 'SET_PIN',
  CHANGE_PIN = 'CHANGE_PIN',
}

export type PinFlowQueryParam =
  | 'verify-pin'
  | 'verify'
  | 'set-pin'
  | 'change-pin';

export type ScreenViewModel = {
  instructionsText: string;
  challengeText: string | null;
  errorMessage: string | null;
  isLoading: boolean;
  maxInputLength: number | undefined;
  isNumericInput: boolean;
  inputPlaceholder: string | undefined;
  inputTextAlign: 'center' | 'left';
  userInput: string;
};

export type RemoteEventsRequest = {
  url?: URL | string;
  options?: RequestInit;
};

export type FlowContext = {
  localizations: Record<string, string>;
  screenGeneral: {
    is_use_digits?: boolean;
    maximum_multiplier?: number;
  };
  pinLength: number;
  remoteEventsRequest: RemoteEventsRequest;
  version: string;
};

export type MathChallengeFlowState = {
  challengeString: string;
  challengeNumber: number;
};

export enum SetPinStep {
  EnterNewPin = 'EnterNewPin',
  ConfirmNewPin = 'ConfirmNewPin',
}

export type SetPinFlowState = {
  step: SetPinStep;
  pendingPin: string | null;
};

export enum ChangePinStep {
  EnterCurrentPin = 'EnterCurrentPin',
  EnterNewPin = 'EnterNewPin',
  ConfirmNewPin = 'ConfirmNewPin',
}

export type ChangePinFlowState = {
  step: ChangePinStep;
  currentPinCode: string | null;
  pendingNewPin: string | null;
};

export type FlowSubmitResult =
  | { type: 'success'; setsLockToken: boolean }
  | {
      type: 'continue';
      clearInput: boolean;
      errorMessage?: string;
      mathState?: MathChallengeFlowState;
      setPinState?: SetPinFlowState;
      changePinState?: ChangePinFlowState;
    };

export type PinCloudEventResult =
  | { kind: 'success'; subject?: string }
  | { kind: 'error'; message: string }
  | { kind: 'network_error'; message: string };

export const MATH_VALIDATION_DELAY_MS = 1000;

const STORED_PIN_KEY = 'ctx/parent_lock.pin';
const SET_PIN_FLOW: PinFlowQueryParam = 'set-pin';
const CHANGE_PIN_FLOW: PinFlowQueryParam = 'change-pin';
const VERIFY_PIN_FLOWS: PinFlowQueryParam[] = ['verify-pin', 'verify'];

// ---------------------------------------------------------------------------
// Flow resolution
// ---------------------------------------------------------------------------

/**
 * Maps a URL flow query parameter to a FlowType.
 */
export function mapFlowQueryParamToFlowType(
  flowParam: string | null
): FlowType | null {
  if (!flowParam) return null;

  if (flowParam === SET_PIN_FLOW) return FlowType.SET_PIN;
  if (flowParam === CHANGE_PIN_FLOW) return FlowType.CHANGE_PIN;
  if (VERIFY_PIN_FLOWS.includes(flowParam as PinFlowQueryParam)) {
    return FlowType.REMOTE_VERIFY;
  }

  return null;
}

/**
 * Resolves which PIN flow should run from URL params and manifest settings.
 */
export function resolveFlowType(input: {
  flowParam: string | null;
  isRemoteTypeEnabled: boolean;
}): FlowType {
  const requestedFlow = mapFlowQueryParamToFlowType(input.flowParam);

  if (requestedFlow) return requestedFlow;

  if (input.isRemoteTypeEnabled) return FlowType.REMOTE_VERIFY;

  return FlowType.MATH_CHALLENGE;
}

// ---------------------------------------------------------------------------
// View model
// ---------------------------------------------------------------------------

type MergeViewModelInput = {
  instructionsText: string;
  challengeText: string | null;
  errorMessage: string | null;
  isLoading: boolean;
  maxInputLength: number | undefined;
  isNumericInput: boolean;
  inputPlaceholder: string | undefined;
  inputTextAlign: 'center' | 'left';
  userInput: string;
};

/**
 * Hides error messages while the user is re-typing after a failed attempt.
 */
export function mergePinFlowViewModel(
  input: MergeViewModelInput
): ScreenViewModel {
  const errorMessage =
    input.userInput.length > 0 && input.errorMessage
      ? null
      : input.errorMessage;

  return {
    instructionsText: input.instructionsText,
    challengeText: input.challengeText,
    errorMessage,
    isLoading: input.isLoading,
    maxInputLength: input.maxInputLength,
    isNumericInput: input.isNumericInput,
    inputPlaceholder: input.inputPlaceholder,
    inputTextAlign: input.inputTextAlign,
    userInput: input.userInput,
  };
}

// ---------------------------------------------------------------------------
// Cloud events
// ---------------------------------------------------------------------------

type SendPinCloudEventInput = {
  remoteEventsRequest: RemoteEventsRequest;
  version: string;
  eventType: string;
  data: Record<string, unknown>;
  subject: string;
};

function getRequestUrl(
  remoteEventsRequest: RemoteEventsRequest
): string | undefined {
  if (!remoteEventsRequest?.url) return undefined;

  return remoteEventsRequest.url instanceof URL
    ? remoteEventsRequest.url.toString()
    : remoteEventsRequest.url;
}

/**
 * Sends a CloudEvents v1.0 PIN request and normalizes the HTTP response.
 */
export async function sendPinCloudEvent({
  remoteEventsRequest,
  version,
  eventType,
  data,
  subject,
}: SendPinCloudEventInput): Promise<PinCloudEventResult> {
  const requestUrl = getRequestUrl(remoteEventsRequest);

  if (!requestUrl) {
    return { kind: 'error', message: 'PIN endpoint is not configured' };
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(remoteEventsRequest?.options?.headers || {}),
  };

  try {
    const response = await fetch(requestUrl, {
      ...remoteEventsRequest?.options,
      method: 'POST',
      headers,
      body: JSON.stringify(
        prepareCloudEventPayload({
          data,
          type: eventType,
          subject,
          source: `${window.location.origin}/version/${version}`,
        })
      ),
    });

    if (response.status === 200 || response.status === 201) {
      let subjectMessage: string | undefined;

      try {
        const json: unknown = await response.json();

        if (
          json &&
          typeof json === 'object' &&
          'subject' in json &&
          typeof (json as { subject?: unknown }).subject === 'string'
        ) {
          subjectMessage = (json as { subject: string }).subject;
        }
      } catch {
        // Response body is optional for success.
      }

      return { kind: 'success', subject: subjectMessage };
    }

    if (response.status === 400) {
      try {
        const json: unknown = await response.json();
        const message = extractErrorMessage(json);

        if (message) {
          return { kind: 'error', message };
        }
      } catch {
        // Fall through to generic error.
      }
    }

    return {
      kind: 'error',
      message: `PIN request failed (${response.status})`,
    };
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : 'Network request failed';

    logger.error(`sendPinCloudEvent: ${message}`);

    return { kind: 'network_error', message };
  }
}

export async function verifyPin(input: {
  remoteEventsRequest: RemoteEventsRequest;
  version: string;
  pinCode: string;
}): Promise<PinCloudEventResult> {
  return sendPinCloudEvent({
    remoteEventsRequest: input.remoteEventsRequest,
    version: input.version,
    eventType: 'com.applicaster.pin.v1',
    subject: 'Pin code sent',
    data: { pin_code: input.pinCode },
  });
}

export async function setPin(input: {
  remoteEventsRequest: RemoteEventsRequest;
  version: string;
  pinCode: string;
}): Promise<PinCloudEventResult> {
  return sendPinCloudEvent({
    remoteEventsRequest: input.remoteEventsRequest,
    version: input.version,
    eventType: 'com.applicaster.pin.set.v1',
    subject: 'Set PIN',
    data: { pin_code: input.pinCode },
  });
}

export async function verifyCurrentPinForChange(input: {
  remoteEventsRequest: RemoteEventsRequest;
  version: string;
  currentPinCode: string;
}): Promise<PinCloudEventResult> {
  return sendPinCloudEvent({
    remoteEventsRequest: input.remoteEventsRequest,
    version: input.version,
    eventType: 'com.applicaster.pin.change.v1',
    subject: 'Verify current PIN',
    data: {
      step: 'verify_current',
      current_pin_code: input.currentPinCode,
    },
  });
}

export async function confirmPinChange(input: {
  remoteEventsRequest: RemoteEventsRequest;
  version: string;
  currentPinCode: string;
  pinCode: string;
}): Promise<PinCloudEventResult> {
  return sendPinCloudEvent({
    remoteEventsRequest: input.remoteEventsRequest,
    version: input.version,
    eventType: 'com.applicaster.pin.change.v1',
    subject: 'Confirm PIN change',
    data: {
      step: 'confirm_change',
      current_pin_code: input.currentPinCode,
      pin_code: input.pinCode,
    },
  });
}

function extractErrorMessage(json: unknown): string | undefined {
  if (!json || typeof json !== 'object') return undefined;

  const record = json as Record<string, unknown>;
  const response = record.response;

  if (response && typeof response === 'object') {
    const responseRecord = response as Record<string, unknown>;
    const data = responseRecord.data;

    if (data && typeof data === 'object') {
      const dataRecord = data as Record<string, unknown>;

      if (typeof dataRecord.message === 'string') {
        return dataRecord.message;
      }
    }
  }

  if (typeof record.message === 'string') {
    return record.message;
  }

  return undefined;
}

// ---------------------------------------------------------------------------
// Math challenge flow
// ---------------------------------------------------------------------------

export function createMathChallengeState(
  context: FlowContext
): MathChallengeFlowState {
  const result = createChallenge(
    {
      is_use_digits: context.screenGeneral.is_use_digits ?? true,
      maximum_multiplier: context.screenGeneral.maximum_multiplier ?? 10,
    },
    context.localizations
  );

  return {
    challengeString: result.string,
    challengeNumber: result.number,
  };
}

export function getMathChallengeInstructions(
  localizations: Record<string, string>
): string {
  return (
    localizations.instructions_text ||
    'To Continue, Please answer the following question'
  );
}

export async function submitMathChallenge(input: {
  state: MathChallengeFlowState;
  value: string;
  localizations: Record<string, string>;
  context: FlowContext;
}): Promise<FlowSubmitResult> {
  await delay(MATH_VALIDATION_DELAY_MS);

  if (input.value !== String(input.state.challengeNumber)) {
    return {
      type: 'continue',
      clearInput: true,
      errorMessage:
        input.localizations.error_message_text ||
        'Incorrect answer, please try again.',
      mathState: createMathChallengeState(input.context),
    };
  }

  return {
    type: 'success',
    setsLockToken: true,
  };
}

// ---------------------------------------------------------------------------
// Remote verify flow
// ---------------------------------------------------------------------------

export function clearStoredPin(): void {
  sessionStorage.removeItem(STORED_PIN_KEY);
}

export function storeVerifiedPin(pinCode: string): void {
  sessionStorage.setItem(STORED_PIN_KEY, pinCode);
}

export function getRemoteVerifyInstructions(
  localizations: Record<string, string>
): string {
  return localizations.remote_type_instructions_text || 'Please enter the Pin';
}

export function getRemoteVerifyErrorMessage(
  localizations: Record<string, string>
): string {
  return (
    localizations.remote_type_error_text ||
    'Something went wrong, please try again.'
  );
}

export async function submitRemoteVerify(input: {
  value: string;
  pinLength: number;
  context: FlowContext;
  localizations: Record<string, string>;
}): Promise<FlowSubmitResult> {
  if (input.pinLength <= 0 || input.value.length !== input.pinLength) {
    return { type: 'continue', clearInput: false };
  }

  const result = await verifyPin({
    remoteEventsRequest: input.context.remoteEventsRequest,
    version: input.context.version,
    pinCode: input.value,
  });

  if (result.kind === 'success') {
    storeVerifiedPin(input.value);

    return {
      type: 'success',
      setsLockToken: true,
    };
  }

  const message =
    result.kind === 'error' || result.kind === 'network_error'
      ? result.message
      : getRemoteVerifyErrorMessage(input.localizations);

  return {
    type: 'continue',
    clearInput: true,
    errorMessage: message,
  };
}

// ---------------------------------------------------------------------------
// Set PIN flow
// ---------------------------------------------------------------------------

export function createSetPinState(): SetPinFlowState {
  return {
    step: SetPinStep.EnterNewPin,
    pendingPin: null,
  };
}

export function getSetPinInstructions(
  state: SetPinFlowState,
  localizations: Record<string, string>
): string {
  if (state.step === SetPinStep.ConfirmNewPin) {
    return (
      localizations.set_pin_confirm_instructions_text ||
      'Please confirm your new PIN'
    );
  }

  return (
    localizations.set_pin_enter_instructions_text || 'Please enter a new PIN'
  );
}

export async function submitSetPin(input: {
  state: SetPinFlowState;
  value: string;
  pinLength: number;
  context: FlowContext;
  localizations: Record<string, string>;
}): Promise<FlowSubmitResult> {
  if (input.pinLength <= 0 || input.value.length !== input.pinLength) {
    return { type: 'continue', clearInput: false };
  }

  if (input.state.step === SetPinStep.EnterNewPin) {
    return {
      type: 'continue',
      clearInput: true,
      setPinState: {
        step: SetPinStep.ConfirmNewPin,
        pendingPin: input.value,
      },
    };
  }

  if (input.state.pendingPin !== input.value) {
    return {
      type: 'continue',
      clearInput: true,
      errorMessage:
        input.localizations.set_pin_mismatch_error_text ||
        'PINs do not match, please try again.',
      setPinState: {
        step: SetPinStep.ConfirmNewPin,
        pendingPin: input.state.pendingPin,
      },
    };
  }

  const result = await setPin({
    remoteEventsRequest: input.context.remoteEventsRequest,
    version: input.context.version,
    pinCode: input.value,
  });

  if (result.kind === 'success') {
    return { type: 'success', setsLockToken: false };
  }

  const message =
    result.kind === 'error' || result.kind === 'network_error'
      ? result.message
      : getRemoteVerifyErrorMessage(input.localizations);

  return {
    type: 'continue',
    clearInput: true,
    errorMessage: message,
    setPinState: {
      step: SetPinStep.ConfirmNewPin,
      pendingPin: input.state.pendingPin,
    },
  };
}

// ---------------------------------------------------------------------------
// Change PIN flow
// ---------------------------------------------------------------------------

export function createChangePinState(): ChangePinFlowState {
  return {
    step: ChangePinStep.EnterCurrentPin,
    currentPinCode: null,
    pendingNewPin: null,
  };
}

export function getChangePinInstructions(
  state: ChangePinFlowState,
  localizations: Record<string, string>
): string {
  switch (state.step) {
    case ChangePinStep.EnterCurrentPin:
      return (
        localizations.change_pin_current_instructions_text ||
        'Please enter your current PIN'
      );
    case ChangePinStep.EnterNewPin:
      return (
        localizations.change_pin_new_instructions_text ||
        'Please enter your new PIN'
      );
    case ChangePinStep.ConfirmNewPin:
      return (
        localizations.change_pin_confirm_instructions_text ||
        'Please confirm your new PIN'
      );
    default:
      return '';
  }
}

export async function submitChangePin(input: {
  state: ChangePinFlowState;
  value: string;
  pinLength: number;
  context: FlowContext;
  localizations: Record<string, string>;
}): Promise<FlowSubmitResult> {
  if (input.pinLength <= 0 || input.value.length !== input.pinLength) {
    return { type: 'continue', clearInput: false };
  }

  if (input.state.step === ChangePinStep.EnterCurrentPin) {
    const result = await verifyCurrentPinForChange({
      remoteEventsRequest: input.context.remoteEventsRequest,
      version: input.context.version,
      currentPinCode: input.value,
    });

    if (result.kind === 'success') {
      return {
        type: 'continue',
        clearInput: true,
        changePinState: {
          step: ChangePinStep.EnterNewPin,
          currentPinCode: input.value,
          pendingNewPin: null,
        },
      };
    }

    return {
      type: 'continue',
      clearInput: true,
      errorMessage: getChangePinErrorMessage(result, input.localizations),
      changePinState: createChangePinState(),
    };
  }

  if (input.state.step === ChangePinStep.EnterNewPin) {
    return {
      type: 'continue',
      clearInput: true,
      changePinState: {
        step: ChangePinStep.ConfirmNewPin,
        currentPinCode: input.state.currentPinCode,
        pendingNewPin: input.value,
      },
    };
  }

  if (input.state.pendingNewPin !== input.value) {
    return {
      type: 'continue',
      clearInput: true,
      errorMessage:
        input.localizations.change_pin_mismatch_error_text ||
        'PINs do not match, please try again.',
      changePinState: {
        step: ChangePinStep.ConfirmNewPin,
        currentPinCode: input.state.currentPinCode,
        pendingNewPin: input.state.pendingNewPin,
      },
    };
  }

  if (!input.state.currentPinCode) {
    return {
      type: 'continue',
      clearInput: true,
      errorMessage: getRemoteVerifyErrorMessage(input.localizations),
      changePinState: createChangePinState(),
    };
  }

  const result = await confirmPinChange({
    remoteEventsRequest: input.context.remoteEventsRequest,
    version: input.context.version,
    currentPinCode: input.state.currentPinCode,
    pinCode: input.value,
  });

  if (result.kind === 'success') {
    return { type: 'success', setsLockToken: false };
  }

  return {
    type: 'continue',
    clearInput: true,
    errorMessage: getChangePinErrorMessage(result, input.localizations),
    changePinState: {
      step: ChangePinStep.ConfirmNewPin,
      currentPinCode: input.state.currentPinCode,
      pendingNewPin: input.state.pendingNewPin,
    },
  };
}

function getChangePinErrorMessage(
  result: { kind: string; message?: string },
  localizations: Record<string, string>
): string {
  if (result.kind === 'error' || result.kind === 'network_error') {
    return result.message || getRemoteVerifyErrorMessage(localizations);
  }

  return getRemoteVerifyErrorMessage(localizations);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

type PinCodeWaiter = {
  resolve: (result: ActionResult) => void;
  hasFollowingActions: boolean;
};

let pinCodeWaiter: PinCodeWaiter | null = null;

export function settlePinCodeResult(result: ActionResult): void {
  const waiter = pinCodeWaiter;
  pinCodeWaiter = null;
  waiter?.resolve(result);
}

export function pendingPinCodeHasFollowingActions(): boolean {
  return pinCodeWaiter?.hasFollowingActions === true;
}

function waitForPinCodeResult(
  hasFollowingActions: boolean
): Promise<ActionResult> {
  settlePinCodeResult(ActionResult.Cancel);

  return new Promise((resolve) => {
    pinCodeWaiter = { resolve, hasFollowingActions };
  });
}

function chainHasFollowingActions(context: unknown): boolean {
  if (!context || typeof context !== 'object') return false;

  return (
    (context as { hasFollowingActions?: boolean }).hasFollowingActions === true
  );
}

export const pinCodeAction: ActionHandler<{
  typeMapping?: string;
  navigationAction?: 'push' | 'replace';
  flow?: string;
}> = (action, ctx) => {
  const { typeMapping, navigationAction, flow } = action.options ?? {};

  if (typeMapping !== 'parent-lock' || !ctx.navigate) {
    return ActionResult.Cancel;
  }

  if (flow === 'reset-pin') {
    return ActionResult.Success;
  }

  if (!flow || !mapFlowQueryParamToFlowType(flow)) {
    return ActionResult.Error;
  }

  ctx.navigate(`/lock?flow=${encodeURIComponent(flow)}`, {
    replace: navigationAction === 'replace',
  });

  return waitForPinCodeResult(chainHasFollowingActions(action.context));
};
